import { useId, useState } from 'react';
import { IconChevronDown, IconChevronUp, IconInfoCircle, IconNotes, IconPencil, IconPin, IconPinned, IconUser } from '@tabler/icons-react';
import { useT } from '@hana/i18n';
import s from './notes.module.css';

export type NoteItem = {
  id: string;
  text: string;
  /** nhãn loại, vd "Điểm dừng" / "Chung" */
  kind: string;
  /** true = ghi chú gắn điểm dừng (đếm ở chip "tại điểm") */
  stop?: boolean;
  by: string;
  at: string;
  pinned?: boolean;
};

export type NotesScope = 'trf' | 'job' | 'vessel' | 'bl' | 'invoice' | 'service' | (string & {});

/**
 * Khối Ghi chú dùng chung (mẫu: Mint-TMS-Trip › Ghi chú).
 * Bản này nhận dữ liệu qua props (mock); bảng ghi chú chung ở schema `hub` sẽ nối sau khi anh duyệt SQL.
 */
export function Notes({
  scope,
  id,
  notes,
  tip,
  onAdd,
  onTogglePin,
  defaultOpen = true,
}: {
  scope: NotesScope;
  id: string;
  notes: NoteItem[];
  /** nội dung tooltip ⓘ */
  tip?: string;
  onAdd?: () => void;
  /** có truyền thì ghim do bên ngoài quản; không thì giữ trạng thái cục bộ */
  onTogglePin?: (id: string, pinned: boolean) => void;
  defaultOpen?: boolean;
}) {
  const tr = useT();
  const [open, setOpen] = useState(defaultOpen);
  const [local, setLocal] = useState<Record<string, boolean>>({});
  const bodyId = useId();
  const stopN = notes.filter((n) => n.stop).length;
  const pinned = (n: NoteItem) => local[n.id] ?? !!n.pinned;
  const toggle = (n: NoteItem) => {
    const next = !pinned(n);
    setLocal((p) => ({ ...p, [n.id]: next }));
    onTogglePin?.(n.id, next);
  };
  return (
    <section aria-label={tr('notes.title')} className={s.card} data-notes-scope={scope} data-notes-id={id}>
      <div className={s.head}>
        <span className={s.ico} aria-hidden="true"><IconNotes size={18} stroke={1.9} /></span>
        <h2 className={s.h2}>{tr('notes.title')}</h2>
        {tip && (
          <span role="img" aria-label={tip} title={tip} className={s.tip}>
            <IconInfoCircle size={14} stroke={1.9} aria-hidden="true" />
          </span>
        )}
        <span className={s.pill}>{tr('notes.count', { n: notes.length })}</span>
        <span className={`${s.pill} ${s.pillInfo}`}>{tr('notes.stopCount', { n: stopN })}</span>
        <div className={s.act}>
          <button type="button" className={s.add} onClick={onAdd} aria-label={tr('notes.add')} title={tr('notes.add')}>
            <IconPencil size={16} stroke={1.8} aria-hidden="true" />
          </button>
          <button type="button" className={s.fold} aria-label={tr('notes.toggle')} aria-expanded={open} aria-controls={bodyId} onClick={() => setOpen((v) => !v)}>
            {open ? <IconChevronUp size={18} stroke={1.9} aria-hidden="true" /> : <IconChevronDown size={18} stroke={1.9} aria-hidden="true" />}
          </button>
        </div>
      </div>
      {open && (
        <div id={bodyId} className={s.body}>
          {notes.map((n) => {
            const on = pinned(n);
            return (
              <div key={n.id} className={s.row}>
                <span className={s.av}><IconUser size={16} stroke={1.9} aria-hidden="true" /></span>
                <span className={s.tx}>
                  <span className={s.t}>{n.text}</span>
                  <span className={s.m}>
                    <span className={s.kind}>{n.kind}</span>
                    <span>{n.by}</span>
                    <span>{n.at}</span>
                  </span>
                </span>
                <button
                  type="button"
                  className={`${s.sq} ${on ? s.sqOn : ''}`}
                  aria-label={tr(on ? 'notes.unpin' : 'notes.pin', { text: n.text })}
                  aria-pressed={on}
                  onClick={() => toggle(n)}
                >
                  {on ? <IconPinned size={16} aria-hidden="true" /> : <IconPin size={16} aria-hidden="true" />}
                </button>
              </div>
            );
          })}
          {notes.length === 0 && <div className={s.empty}>{tr('notes.empty')}</div>}
        </div>
      )}
    </section>
  );
}
